import * as React from "react";
import { Alert, AlertTitle, Link, Stack, Typography } from "@mui/material";
import { useParams, Link as RouterLink } from "react-router-dom";
import DetailPage from "../../../components/common/detailPage/detailPage";
import { SimpleBreadcrumbs } from "../../../components/navigation/simpleBreadcrumbs";
import ResourceInfoPanel from "./info/resourceInfoPanel";
import StandardLayout from "../../../layouts/standard";
import AttributesPanel from "./attributes/AttributesPanel";
import ResourceBio from "./bios/resourceBio";
import Assignments from "./assignments/assignments";
import PageLoader from "../../../components/common/pageLoader";
import { Profile } from "../../../services/types/enterpriseCoachingApiTypes";
import { useGetDemographicsByProfileKeyQuery } from "../../../services/cclTokenedEnterpriseCoachingApi";
import { getErrorMsg } from "../../../utilities/rtkQueryErrorHelpers";
import {
    eGroupIds,
    UserClaimsService,
} from "../../../services/currentUserService/currentUserService";
import ResourceWorkPoolsPanel from "./workPools/resourceWorkPools";
import useCombinationKeyPress from "../../../hooks/useCombinationKeyPress";
import ResourceEventCalendar from "../../../components/shared/resourceEventCalendar/resourceEventCalendar";
import ResourceNotesLogsCard from "./notes/resourceLogsCard";
import ResourceNotesEntryTimelineCard from "./notes/resourceEntryTimelineCard";

export type ResourceDetailProps = {
    pKey: string;
    target: string;
};

const ResourceDetail: React.FC = (props) => {
    const params = useParams<ResourceDetailProps>();
    const pKey = params.pKey !== undefined ? params.pKey : "";
    const target: string =
        params.target !== undefined && !isNaN(Number(params.target)) ? params.target : "0";
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);

    const { data, isError, error, isLoading, refetch } = useGetDemographicsByProfileKeyQuery(pKey);
    const detailEl = React.useRef<HTMLDivElement>(null);

    const onKeyDown = (event: KeyboardEvent) => {
        setDrawerOpen(true);
        event.preventDefault();
    };
    const claimsService = new UserClaimsService();

    useCombinationKeyPress(["j"], onKeyDown, detailEl.current);
    const emptyProfile = {} as Profile;
    const profile = data == null ? emptyProfile : data;
    const email = profile.email ? profile.email : "";

    const fullTablist: [string, number, React.ReactNode][] = [
        [
            "Details",
            0,
            <ResourceInfoPanel profile={profile} pKey={pKey} refetchProfile={refetch} />,
        ],
        [
            "Attributes",
            1,
            <AttributesPanel imKey={data ? data.imKey.toString() : ""} pKey={pKey} email={email} />,
        ],
        [
            "Bio",
            2,
            <ResourceBio pKey={pKey} imKey={data ? data.imKey.toString() : ""} email={email} />,
        ],
        ["Work Pools", 3, <ResourceWorkPoolsPanel pkey={pKey} />],
        ["Assignments", 4, <Assignments profile={profile} pkey={pKey} />],
        [
            "Calendar",
            5,
            <ResourceEventCalendar imKey={data ? data.imKey.toString() : ""} pKey={pKey} />,
        ],
        [
            "Notes",
            6,
            <ResourceNotesEntryTimelineCard
                imKey={data ? data.imKey.toString() : ""}
                pKey={pKey}
                email={email}
                drawerOpen={drawerOpen}
                onDrawerClose={() => setDrawerOpen(false)}
            />,
        ],
        ["Events", 7, <ResourceNotesLogsCard imKey={data ? data.imKey.toString() : ""} />],
    ];

    const cclAdminBaseTabList = fullTablist.filter(
        (x) =>
            x[0] === "Details" ||
            x[0] === "Attributes" ||
            x[0] === "Bio" ||
            x[0] === "Assignments" ||
            x[0] === "Calendar",
    );

    const resourceManagerTabList = fullTablist.filter(
        (x) =>
            x[0] === "Details" ||
            x[0] === "Attributes" ||
            x[0] === "Bio" ||
            x[0] === "Work Pools" ||
            x[0] === "Assignments" ||
            x[0] === "Calendar" ||
            x[0] === "Notes" ||
            x[0] === "Events",
    );

    const workPoolManagerTabList = fullTablist.filter(
        (x) =>
            x[0] === "Details" ||
            x[0] === "Attributes" ||
            x[0] === "Bio" ||
            x[0] === "Work Pools" ||
            x[0] === "Assignments" ||
            x[0] === "Calendar",
    );
    const minimalTabList = fullTablist.filter(
        (x) => x[0] === "Details" || x[0] === "Bio" || x[0] === "Assignments",
    );

    const isUserAdmin = claimsService.IsUserInGroups([eGroupIds.eCCLAdmin]);

    let tablist: [string, number, React.ReactNode][] = minimalTabList;

    if (claimsService.IsUserSuperAdmin()) {
        tablist = fullTablist;
    } else if (isUserAdmin && claimsService.IsUserInAppRole(10)) {
        tablist = resourceManagerTabList;
    } else if (isUserAdmin && claimsService.IsUserInAppRole(1)) {
        tablist = workPoolManagerTabList;
    } else if (isUserAdmin) {
        tablist = cclAdminBaseTabList;
    }

    function showErrorAlert(msg: string | undefined) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <div>
                    <Alert severity="error" sx={{ fontSize: 14 }}>
                        <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                        {msg}
                    </Alert>
                </div>
            </StandardLayout>
        );
    }

    const breadcrumbs = (
        <Stack direction={"row"} spacing={3}>
            <SimpleBreadcrumbs>
                <Typography variant="h6" color="text.primary">
                    <Link key="findResourceLink" component={RouterLink} to="/resources">
                        Resources
                    </Link>
                </Typography>
                <Typography variant="h6" color="text.primary">
                    {profile != null ? profile.firstName + " " + profile.lastName : ""}
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    if (isError) {
        return showErrorAlert(`Error retrieving ${pKey} (${getErrorMsg(error, "")})`);
    }

    if (isLoading) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <PageLoader msg={""} />
            </StandardLayout>
        );
    }

    return (
        <div ref={detailEl}>
            <DetailPage
                breadcrumbs={breadcrumbs}
                descriptor={"resources"}
                tablist={tablist}
                activeTab={+target}
                identifier="resources-details"
            ></DetailPage>
        </div>
    );
};

export default ResourceDetail;
